import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import PayrollRunDto from '@/models/Payroll/PayrollRunDto';
import PayrollPeriodInfoDto from '@/models/Payroll/PayrollPeriodInfoDto';

export interface PayrollRunListFilters {
    status: number | null;
    periodFrom: string | null;
    periodTo: string | null;
}

export interface IPayrollRunListState {
    loading: boolean;
    runs: PayrollRunDto[];
    filters: PayrollRunListFilters;
    currentPeriod: PayrollPeriodInfoDto | null;
    currentPeriodLoading: boolean;
    currentPeriodError: string;
}

function defaultFilters(): PayrollRunListFilters {
    return { status: null, periodFrom: null, periodTo: null };
}

@Module({ dynamic: true, store, name: 'PayrollRunList', namespaced: true })
class PayrollRunList extends VuexModule implements IPayrollRunListState {
    public loading = false;
    public runs: PayrollRunDto[] = [];
    public filters: PayrollRunListFilters = defaultFilters();
    // The provider's currently-open pay period, resolved live when the New
    // Payroll Run dialog opens. Kept with its own loading/error state so the
    // dialog spinner doesn't fight the list's loading flag.
    public currentPeriod: PayrollPeriodInfoDto | null = null;
    public currentPeriodLoading = false;
    public currentPeriodError = '';

    @Mutation
    setLoading(v: boolean) { this.loading = v; }

    @Mutation
    setRuns(items: PayrollRunDto[]) { this.runs = items; }

    @Mutation
    setFilters(f: PayrollRunListFilters) { this.filters = f; }

    @Mutation
    setCurrentPeriod(p: PayrollPeriodInfoDto | null) { this.currentPeriod = p; }

    @Mutation
    setCurrentPeriodLoading(v: boolean) { this.currentPeriodLoading = v; }

    @Mutation
    setCurrentPeriodError(msg: string) { this.currentPeriodError = msg; }

    @Action
    async setFiltersAndFetch(f: PayrollRunListFilters) {
        this.setFilters(f);
        await this.fetch();
    }

    @Action
    async fetch() {
        this.setLoading(true);
        try {
            const params: string[] = [];
            if (this.filters.status != null) params.push(`status=${this.filters.status}`);
            if (this.filters.periodFrom) params.push(`periodFrom=${this.filters.periodFrom}`);
            if (this.filters.periodTo) params.push(`periodTo=${this.filters.periodTo}`);
            const qs = params.length ? `?${params.join('&')}` : '';
            const response = await ajax.get(`/api/v1/agency/payroll-runs${qs}`);
            this.setRuns(response.data || []);
        } catch (error) {
            AppModule.handleApiError(error);
            this.setRuns([]);
        } finally {
            this.setLoading(false);
        }
    }

    // Resolve the provider's currently-open pay period for the New Payroll
    // Run dialog. Errors land in currentPeriodError (shown inline in the
    // dialog) rather than the global alert — a failed period lookup is a
    // dialog-level condition the clerk needs to see in place.
    @Action
    async fetchCurrentPeriod() {
        this.setCurrentPeriodLoading(true);
        this.setCurrentPeriod(null);
        this.setCurrentPeriodError('');
        try {
            const response = await ajax.get('/api/v1/agency/payroll-runs/current-period');
            this.setCurrentPeriod(response.data as PayrollPeriodInfoDto);
        } catch (error) {
            const e = error as any;
            const msg = (e.response && e.response.data && e.response.data.message)
                ? e.response.data.message
                : 'Could not read the current period from the payroll system.';
            this.setCurrentPeriodError(msg);
        } finally {
            this.setCurrentPeriodLoading(false);
        }
    }

    // Draft creation — the clerk confirms the provider-resolved period shown
    // read-only on the dialog; only the period ID is sent. The backend
    // re-resolves the period's dates from the provider itself (client dates
    // are not accepted — a stale or mistyped date range is how runs end up
    // in the wrong tax month). Returns the created run so the caller can
    // route into it.
    @Action
    async createDraft(payload: {
        externalPayrollSystemPeriodId: string;
    }): Promise<PayrollRunDto | null> {
        this.setLoading(true);
        try {
            const response = await ajax.post('/api/v1/agency/payroll-runs', {
                externalPayrollSystemPeriodId: payload.externalPayrollSystemPeriodId,
            });
            await this.fetch();
            return response.data as PayrollRunDto;
        } catch (error) {
            AppModule.handleApiError(error);
            return null;
        } finally {
            this.setLoading(false);
        }
    }

    @Action
    cleanState() {
        this.context.commit('resetState');
    }

    @Mutation
    resetState() {
        this.loading = false;
        this.runs = [];
        this.filters = defaultFilters();
        this.currentPeriod = null;
        this.currentPeriodLoading = false;
        this.currentPeriodError = '';
    }
}

export const PayrollRunListModule = getModule(PayrollRunList);
